import { datadogRum, type RumEvent } from "@datadog/browser-rum";
import { nextjsPlugin, onRouterTransitionStart } from "@datadog/browser-rum-nextjs";

export { onRouterTransitionStart };

type UserInfo = {
  username?: string;
  name?: string;
  homeAccountId?: string;
};

function parseNameParts(fullName: string | undefined): { firstName?: string; lastName?: string } {
  if (!fullName) {
    return {};
  }

  const tokens = fullName.trim().split(/\s+/).filter(Boolean);

  if (tokens.length === 0) {
    return {};
  }

  if (tokens.length === 1) {
    return { firstName: tokens[0] };
  }

  return {
    firstName: tokens[0],
    lastName: tokens[tokens.length - 1],
  };
}

function extractTenantId(homeAccountId: string | undefined): string | undefined {
  if (!homeAccountId) {
    return undefined;
  }

  const parts = homeAccountId.split(".");
  const tenant = parts[parts.length - 1];
  return tenant && tenant !== homeAccountId ? tenant : undefined;
}

const RUM_SERVICE = "architect-web";
const ACCOUNT_NAME = "Machina Labs";
const SESSION_SAMPLE_RATE = 100;
const SESSION_REPLAY_SAMPLE_RATE = 100;
const TRACE_SAMPLE_RATE = 100;
const DEFAULT_PRIVACY_LEVEL = "allow";
const DATADOG_DEBUG = process.env.NEXT_PUBLIC_DATADOG_DEBUG === "true";

function debugLog(message: string, data?: unknown) {
  if (!DATADOG_DEBUG) {
    return;
  }

  if (data !== undefined) {
    console.info(`[Datadog RUM] ${message}`, data);
    return;
  }

  console.info(`[Datadog RUM] ${message}`);
}

function getCookieValue(name: string): string | null {
  const cookies = document.cookie.split(";");
  const cookie = cookies.find((entry) => entry.trim().startsWith(`${name}=`));
  if (!cookie) {
    return null;
  }

  const rawValue = cookie.split("=").slice(1).join("=");
  return decodeURIComponent(rawValue);
}

function readUserInfo(): UserInfo | null {
  const rawUserInfo = getCookieValue("userInfo");
  if (!rawUserInfo) {
    return null;
  }

  try {
    return JSON.parse(rawUserInfo) as UserInfo;
  } catch {
    return null;
  }
}

function redactQueryParam(url: string, key: string): string {
  const paramRegex = new RegExp(`([?&]${key}=)[^&]*`, "gi");
  return url.replace(paramRegex, "$1REDACTED");
}

function redactRumEvent(event: RumEvent): RumEvent {
  const queryParamsToRedact = ["token", "signature", "sig", "auth"];
  const resource = event.resource as { url?: string } | undefined;
  const error = event.error as { resource?: { url?: string } } | undefined;

  if (event.view?.url) {
    for (const queryParam of queryParamsToRedact) {
      event.view.url = redactQueryParam(event.view.url, queryParam);
    }
  }

  if (resource?.url) {
    for (const queryParam of queryParamsToRedact) {
      resource.url = redactQueryParam(resource.url, queryParam);
    }
  }

  if (error?.resource?.url) {
    for (const queryParam of queryParamsToRedact) {
      error.resource.url = redactQueryParam(error.resource.url, queryParam);
    }
  }

  return event;
}

function initializeDatadogRum() {
  if (typeof window === "undefined") {
    return;
  }

  const rumWindow = window as Window & {
    __ARCHITECT_DATADOG_RUM_INITIALIZED__?: boolean;
    __ARCHITECT_BUILD_SHA_LOGGED__?: boolean;
  };

  const version = process.env.NEXT_PUBLIC_DATADOG_VERSION || "local";

  if (process.env.NODE_ENV === "production" && !rumWindow.__ARCHITECT_BUILD_SHA_LOGGED__) {
    console.info(`[Architect] Build SHA: ${version}`);
    rumWindow.__ARCHITECT_BUILD_SHA_LOGGED__ = true;
  }

  if (rumWindow.__ARCHITECT_DATADOG_RUM_INITIALIZED__) {
    return;
  }

  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
  const site = process.env.NEXT_PUBLIC_DATADOG_SITE || "us3.datadoghq.com";

  if (!applicationId || !clientToken) {
    debugLog("Skipped init (missing applicationId/clientToken)", {
      hasApplicationId: Boolean(applicationId),
      hasClientToken: Boolean(clientToken),
    });
    return;
  }

  const env = process.env.NEXT_PUBLIC_DATADOG_ENV || process.env.NODE_ENV || "development";

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service: RUM_SERVICE,
    env,
    version,
    sessionSampleRate: SESSION_SAMPLE_RATE,
    sessionReplaySampleRate: SESSION_REPLAY_SAMPLE_RATE,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    trackingConsent: "granted",
    defaultPrivacyLevel: DEFAULT_PRIVACY_LEVEL,
    allowedTracingUrls: [window.location.origin],
    traceSampleRate: TRACE_SAMPLE_RATE,
    plugins: [nextjsPlugin()],
    beforeSend: (event) => {
      redactRumEvent(event);
      return true;
    },
  });

  const userInfo = readUserInfo();

  if (userInfo?.homeAccountId || userInfo?.username) {
    const { firstName, lastName } = parseNameParts(userInfo.name);

    datadogRum.setUser({
      id: userInfo.homeAccountId || userInfo.username || "unknown",
      ...(userInfo.name ? { name: userInfo.name } : {}),
      ...(userInfo.username ? { email: userInfo.username } : {}),
      ...(firstName ? { first_name: firstName } : {}),
      ...(lastName ? { last_name: lastName } : {}),
    });

    const accountId = extractTenantId(userInfo.homeAccountId);
    if (accountId) {
      datadogRum.setAccount({ id: accountId, name: ACCOUNT_NAME });
      debugLog("account set", { accountId });
    }
  }

  rumWindow.__ARCHITECT_DATADOG_RUM_INITIALIZED__ = true;
  debugLog("Initialization completed");
}

initializeDatadogRum();
